import React from 'react';
import Avatar from '../components/Avatar';
import SelectEntityOption from '../components/SelectEntityOption';
import UserBadge from './profile/UserBadge';
import { useGetUser } from './useAllUsers';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import { UserMatchKey } from '../search/entitySearch/localEntitySearch';
import LocalSearchHighlight from '../search/LocalSearchHighlight';
import type { FuzzySearchMatch } from '../utils/search/fuzzySearch';

const USER_FRAGMENT = gql(`
  fragment UserSelectOption_user on User {
    id
    ...useGetUser_user
  }
`);

interface UserSelectOptionProps {
  name?: React.ReactNode;
  noAccess?: boolean;
  showBadge?: boolean;
  user: DocumentType<typeof USER_FRAGMENT>;
  matches?: readonly FuzzySearchMatch[];
}

const UserSelectOption = React.forwardRef<
  HTMLDivElement,
  UserSelectOptionProps
>(
  (
    {
      name,
      noAccess,
      showBadge,
      user,
      matches = [],
      ...props
    }: UserSelectOptionProps,
    ref,
  ) => {
    const getUser = useGetUser();
    const fullUser = getUser(user);

    let secondary = null;
    if (noAccess) {
      secondary = 'Cannot access this post';
    } else if (matches.some((match) => match.key === UserMatchKey.Title)) {
      secondary = (
        <LocalSearchHighlight
          matches={matches}
          matchKey={UserMatchKey.Title}
          defaultValue={fullUser.title}
        />
      );
    } else if (matches.some((match) => match.key === UserMatchKey.Email)) {
      secondary = (
        <LocalSearchHighlight
          matches={matches}
          matchKey={UserMatchKey.Email}
          defaultValue={fullUser.email}
        />
      );
    } else {
      secondary = fullUser.title;
    }

    return (
      <SelectEntityOption
        {...props}
        disabled={noAccess}
        icon={<Avatar user={fullUser} />}
        label={
          <>
            <LocalSearchHighlight
              matches={matches}
              matchKey={UserMatchKey.Name}
              defaultValue={name}
            />
            {showBadge && <UserBadge user={fullUser} />}
          </>
        }
        secondary={secondary}
        ref={ref}
      />
    );
  },
);

export default UserSelectOption;
